import React, { useState, useEffect } from "react";
import { Sparkles, Calendar, HeartPulse, UserCircle, MapPin, Phone, Video, ShieldCheck, X, LogIn, LogOut, UserPlus, Calculator, Stethoscope } from "lucide-react";
import { IQDoctorLogo } from "./IQDoctorLogo";
import PatientChatbot from "./PatientChatbot";
import OnlineConsultation from "./OnlineConsultation";
import { motion } from "motion/react";
import { playAiHoverSound } from "../utils/aiAudio";
import { auth } from "../lib/firebase";
import { onAuthStateChanged, signOut, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import AuthModal from "./AuthModal";
import MedicalNewsBanner from "./MedicalNewsBanner";
import DoctorsPortalView from "./DoctorsPortalView";

export default function PublicPortal() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isOnlineVisitOpen, setIsOnlineVisitOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isDoctorsPortalOpen, setIsDoctorsPortalOpen] = useState(false);
  
  // Patient EHR Dashboard states
  const [lookupId, setLookupId] = useState("");
  const [patientData, setPatientData] = useState<any | null>(null);
  const [appointmentsList, setAppointmentsList] = useState<any[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Secure OTP and Telegram link integration state holders
  const [needOtp, setNeedOtp] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [requireTelegramLink, setRequireTelegramLink] = useState(false);
  const [tempPatientId, setTempPatientId] = useState("");
  const [tempPatientName, setTempPatientName] = useState("");
  const [showDoctorDemo, setShowDoctorDemo] = useState(false);

  // Firebase SMS states
  const [smsOtpValue, setSmsOtpValue] = useState("");
  const [useSmsMode, setUseSmsMode] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<any>(null);

  const [whiteLabel, setWhiteLabel] = useState<any>(null);
  const [currentBannerSlide, setCurrentBannerSlide] = useState(0);

  useEffect(() => {
    if (whiteLabel?.heroBannerEnabled && whiteLabel?.heroBannerImages?.length > 1) {
      const timer = setInterval(() => {
        setCurrentBannerSlide(prev => (prev + 1) % whiteLabel.heroBannerImages.length);
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [whiteLabel?.heroBannerEnabled, whiteLabel?.heroBannerImages]);

  // Auth State
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Family State
  const [allFamilyMembers, setAllFamilyMembers] = useState<any[]>([]);
  const [allAppointments, setAllAppointments] = useState<any[]>([]);
  const [allRecords, setAllRecords] = useState<any[]>([]);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [recordsList, setRecordsList] = useState<any[]>([]);
  const [ordersList, setOrdersList] = useState<any[]>([]);
  const [isAddFamilyModalOpen, setIsAddFamilyModalOpen] = useState(false);
  const [newFamilyMember, setNewFamilyMember] = useState({ name: '', dob: '', nationalId: '', relationship: 'ابن', phone: '' });
  const [addFamilyLoading, setAddFamilyLoading] = useState(false);
  const [addFamilyError, setAddFamilyError] = useState('');

  // Phone Edit State
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhoneValue, setNewPhoneValue] = useState("");
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [updatePhoneError, setUpdatePhoneError] = useState("");
  
  // Custom Avatar Upload State
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState("");
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user?.email) {
        // Auto-login to medical file if email is linked
        fetch("/api/patient/lookup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: user.email, firebaseVerified: true })
        })
        .then(res => res.json())
        .then(data => {
           if (data.success) {
             setAllFamilyMembers(data.familyMembers || [data.patient]);
             setAllAppointments(data.appointments || []);
             setAllRecords(data.clinicalRecords || []);
             setAllOrders(data.orders || []);
             setPatientData(data.patient);
             setAppointmentsList((data.appointments || []).filter((a: any) => a.patientId === data.patient.id));
             setRecordsList((data.clinicalRecords || []).filter((r: any) => r.patientId === data.patient.id));
             setOrdersList((data.orders || []).filter((o: any) => o.patientId === data.patient.id));
             setNeedOtp(false);
             setRequireTelegramLink(false);
           }
        })
        .catch(console.error);
      } else {
        setPatientData(null);
        setAppointmentsList([]);
        setRecordsList([]);
        setOrdersList([]);
        setAllFamilyMembers([]);
        setAllAppointments([]);
        setAllRecords([]);
        setAllOrders([]);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // 1. Load from cache for instant display
    const saved = localStorage.getItem("clinic_white_label_settings");
    if (saved) {
      try {
        setWhiteLabel(JSON.parse(saved));
      } catch (e) {}
    }
    
    // 2. Fetch fresh data from backend (so visitors on mobile get the actual images)
    fetch("/api/clinic/profile")
      .then(res => res.json())
      .then(data => {
        if (data && Object.keys(data).length > 0) {
          setWhiteLabel(data);
          localStorage.setItem("clinic_white_label_settings", JSON.stringify(data));
        }
      })
      .catch(err => console.error("Failed to fetch clinic profile on public portal:", err));

    const loadWhiteLabel = () => {
      const saved2 = localStorage.getItem("clinic_white_label_settings");
      if (saved2) {
        try {
          setWhiteLabel(JSON.parse(saved2));
        } catch (e) {}
      }
    };
    window.addEventListener("storage", loadWhiteLabel);
    return () => window.removeEventListener("storage", loadWhiteLabel);
  }, []);

  // Dynamic status refresh for Telegram updates
  const handleRefreshActivation = async (patId: string) => {
    try {
      const res = await fetch("/api/patient/check-telegram-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patientId: patId })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPatientData((prev: any) => prev ? {
          ...prev,
          telegramChatId: data.telegramChatId
        } : null);
        if (data.telegramChatId) {
          setRequireTelegramLink(false);
        }
      }
    } catch (e) {
      console.error("Refresh lookup error", e);
    }
  };

  const handleLookupPatient = async (e: React.FormEvent, isVerifyingOtp = false) => {
    if (e) e.preventDefault();
    if (!lookupId.trim()) return;
    setLookupLoading(true);
    setLookupError(null);
    try {
      const payload: any = { identifier: lookupId };
      if (isVerifyingOtp) {
        payload.otp = otpValue;
      }
      const res = await fetch("/api/patient/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        if (data.otpRequired) {
          setNeedOtp(true);
          setTempPatientId(data.patientId);
          setRequireTelegramLink(false);
          setLookupError(null);
        } else if (data.success) {
          setAllFamilyMembers(data.familyMembers || [data.patient]);
          setAllAppointments(data.appointments || []);
          setAllRecords(data.clinicalRecords || []);
          setAllOrders(data.orders || []);
          setPatientData(data.patient);
          setAppointmentsList((data.appointments || []).filter((a: any) => a.patientId === data.patient.id));
          setRecordsList((data.clinicalRecords || []).filter((r: any) => r.patientId === data.patient.id));
          setOrdersList((data.orders || []).filter((o: any) => o.patientId === data.patient.id));
          setNeedOtp(false);
          setOtpValue("");
          setRequireTelegramLink(false);
          setLookupError(null);
        }
      } else {
        if (res.status === 403 && data.requireTelegramLink) {
          setRequireTelegramLink(true);
          setTempPatientId(data.patientId);
          setTempPatientName(data.patientName || "");
          setNeedOtp(false);
        }
        setLookupError(data.error || "نعتذر، لم نجد أي ملف طبي بهذا الرقم. يرجى مراجعة الاستقبال.");
      }
    } catch (err) {
      console.error("Patient dashboard lookup error:", err);
      setLookupError("حدث خطأ في الاتصال بالشبكة. يرجى المحاولة لاحقاً.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAddFamilySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFamilyMember.name || !newFamilyMember.dob || !currentUser?.email) return;

    const nameParts = newFamilyMember.name.trim().split(/\s+/);
    if (nameParts.length < 3) {
      setAddFamilyError('الرجاء إدخال الاسم الثلاثي الصحيح (على الأقل 3 مقاطع) لتجنب خلط الملفات.');
      return;
    }

    if (newFamilyMember.relationship === 'زوجة' || newFamilyMember.relationship === 'غير ذلك') {
      if (!newFamilyMember.phone || !newFamilyMember.nationalId) {
        setAddFamilyError('الرجاء إدخال رقم الهاتف صحيح والرقم الوطني الموحد كونه إجبارياً للزوجة أو الفئات الأخرى.');
        return;
      }
    }

    setAddFamilyLoading(true);
    setAddFamilyError('');
    try {
      const res = await fetch("/api/patient/add-family-member", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newFamilyMember, parentEmail: currentUser.email })
      });
      const data = await res.json();
      if (data.success) {
        const newMembers = [...allFamilyMembers, data.patient];
        setAllFamilyMembers(newMembers);
        setPatientData(data.patient); // Switch to the new member automatically
        setAppointmentsList([]); // New member has no appointments yet
        setRecordsList([]);
        setOrdersList([]);
        setIsAddFamilyModalOpen(false);
        setNewFamilyMember({ name: '', dob: '', nationalId: '', relationship: 'ابن', phone: '' });
        
        // Wait 1.8 seconds to allow the backend's 1.5s writeDB debounce to finish and sync with Firestore.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("family_members_updated"));
        }, 1800);
      } else {
        setAddFamilyError(data.error || "حدث خطأ أثناء إضافة الفرد.");
      }
    } catch (err) {
      setAddFamilyError("خطأ في الاتصال بالشبكة.");
    } finally {
      setAddFamilyLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!currentUser || !patientData) return;

    if (file.size > 5 * 1024 * 1024) {
      setAvatarUploadError("حجم الصورة يجب أن لا يتجاوز 5 ميجابايت.");
      return;
    }

    setAvatarUploadError("");
    setIsUploadingAvatar(true);

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 256;
          const MAX_HEIGHT = 256;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, width, height);
          
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
          
          // Show preview immediately for better UX
          setPatientData((prev: any) => ({ ...prev, profileImageUrl: dataUrl }));
          setAllFamilyMembers((prev: any[]) => prev.map(p => p.id === patientData.id ? { ...p, profileImageUrl: dataUrl } : p));
          
          try {
             const res = await fetch("/api/patient/update-avatar", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ patientId: patientData.id, email: currentUser.email, avatarBase64: dataUrl })
             });
             const data = await res.json();
             if (data.success) {
                // Success
             } else {
                setAvatarUploadError(data.error || "فشل الرفع");
             }
          } catch(err) {
             setAvatarUploadError("حدث خطأ في الاتصال");
          } finally {
             setIsUploadingAvatar(false);
             if (e.target) e.target.value = '';
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setAvatarUploadError("حدث خطأ أثناء معالجة الصورة");
      setIsUploadingAvatar(false);
    }
  };

  // Track pending clinical action waiting for privacy consent
  const [pendingAction, setPendingAction] = useState<"chat" | "video" | null>(null);

  // Helper to handle privacy verification before executing a clinical action
  const handleOpenChat = () => {
    setIsChatOpen(true);
  };

  const handleRequestSms = async () => {
    try {
      setLookupLoading(true);
      setLookupError(null);
      
      // Use the statically imported auth and firebase/auth to avoid mixed dynamic/static import TDZ issues

      if (!(window as any).recaptchaVerifier) {
        (window as any).recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible'
        });
      }

      const appVerifier = (window as any).recaptchaVerifier;
      let phoneStr = lookupId.trim();
      if (phoneStr.startsWith("0")) phoneStr = phoneStr.substring(1);
      if (phoneStr.startsWith("+964")) phoneStr = phoneStr.substring(4);
      if (phoneStr.startsWith("00964")) phoneStr = phoneStr.substring(5);
      const phoneNumber = `+964${phoneStr}`;

      const confResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confResult);
      setUseSmsMode(true);
      setNeedOtp(false);
      
    } catch (err: any) {
      console.error(err);
      setLookupError("فشل إرسال رسالة SMS. تأكد من صحة رقمك وتوفر اتصال بالإنترنت.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleVerifySms = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLookupLoading(true);
      setLookupError(null);
      await confirmationResult.confirm(smsOtpValue);
      
      // Phone confirmed securely via Firebase! Tell backend to bypass Telegram check.
      const res = await fetch("/api/patient/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: lookupId, firebaseVerified: true })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPatientData(data.patient);
        setAppointmentsList(data.appointments || []);
        setRecordsList(data.clinicalRecords || []);
        setOrdersList(data.orders || []);
        setUseSmsMode(false);
        setSmsOtpValue("");
        setRequireTelegramLink(false);
      } else {
        setLookupError(data.error || "خطأ في استرجاع الملف الطبي.");
      }
    } catch (err) {
      console.error(err);
      setLookupError("رمز التأكيد غير صحيح أو منتهي الصلاحية.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleOpenVideo = () => {
    try {
      const accepted = localStorage.getItem("iq_doctor_privacy_accepted") === "true";
      if (accepted) {
        setIsOnlineVisitOpen(true);
      } else {
        setPendingAction("video");
        setIsPrivacyOpen(true);
      }
    } catch (e) {
      console.error("Privacy lookup failed", e);
      setIsOnlineVisitOpen(true);
    }
  };

  useEffect(() => {
    if (window.location.search.includes('online-visit')) {
      try {
        const accepted = localStorage.getItem("iq_doctor_privacy_accepted") === "true";
        if (accepted) {
          setIsOnlineVisitOpen(true);
        } else {
          setPendingAction("video");
          setIsPrivacyOpen(true);
        }
      } catch (e) {
        setIsOnlineVisitOpen(true);
      }
      // clean url
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (isDoctorsPortalOpen) {
    return <DoctorsPortalView onBack={() => setIsDoctorsPortalOpen(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans select-none" dir="rtl">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200 shrink-0 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-transparent w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
              <IQDoctorLogo className="w-full h-full" />
            </div>
            <div>
              <h1 className="font-extrabold text-[#0F172A] text-sm md:text-base leading-tight">الدكتور واثق الجلبي</h1>
              <p className="text-[10px] text-slate-500 font-medium">الموقع الألكتروني الخاص</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-6 md:py-12 flex flex-col items-center justify-center relative overflow-hidden md:overflow-visible">
        
        {/* Clinic Hero Banner */}
        {whiteLabel?.heroBannerEnabled && (
          <div className="w-full max-w-3xl mx-auto mb-10 bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 relative group animate-in slide-in-from-top-8 fade-in duration-700">
            {whiteLabel.heroBannerImages && whiteLabel.heroBannerImages.length > 0 ? (
              <div className="relative w-full h-[220px] md:h-[320px]">
                {whiteLabel.heroBannerImages.map((img, idx) => (
                  <img 
                    key={idx}
                    src={img} 
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${currentBannerSlide === idx ? "opacity-100" : "opacity-0"}`} 
                  />
                ))}
                {whiteLabel.heroBannerImages.length > 1 && (
                  <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2 z-20">
                    {whiteLabel.heroBannerImages.map((_, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => setCurrentBannerSlide(idx)}
                        className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all ${currentBannerSlide === idx ? "bg-white scale-150" : "bg-white/50 hover:bg-white/90"}`}
                      />
                    ))}
                  </div>
                )}
                {/* Gradient overlay for text readability */}
                {whiteLabel.heroBannerText && <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent z-10" />}
              </div>
            ) : (
              <div className="w-full h-32 bg-gradient-to-r from-blue-600 to-indigo-600 relative">
                {whiteLabel.heroBannerText && <div className="absolute inset-0 bg-slate-900/20 z-10" />}
              </div>
            )}
            
            {whiteLabel.heroBannerText && (
              <div className="absolute bottom-0 inset-x-0 p-5 md:p-8 z-20 text-center flex flex-col items-center justify-end h-full pointer-events-none">
                <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20 shadow-2xl transform transition-transform hover:scale-105 pointer-events-auto cursor-default">
                  <h2 className="text-white font-extrabold text-lg md:text-2xl leading-tight drop-shadow-lg text-balance">
                    {whiteLabel.heroBannerText}
                  </h2>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Spotlight Hero Centered Section */}
        <div className="text-center w-full max-w-3xl mx-auto mb-10 flex flex-col items-center relative z-10">
          
          {/* Symmetrical AI Constellation Layout and Centered Logo Core */}
          <div className="relative w-full max-w-2xl h-[330px] flex items-center justify-center mb-6 overflow-visible">
            
            {/* Elegant curvy digital connection lines behind the nodes */}
            <svg className="absolute inset-0 w-full h-full opacity-35 hidden md:block" viewBox="0 0 600 330" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="clinical-glow-lines" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#818cf8" stopOpacity="0.1" />
                  <stop offset="35%" stopColor="#22d3ee" stopOpacity="0.4" />
                  <stop offset="50%" stopColor="#6366f1" stopOpacity="0.6" />
                  <stop offset="65%" stopColor="#22d3ee" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#818cf8" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              {/* Swooping bezier curves representing AI routing channels to Iraqi Governorates (formed as a symmetrical arc) */}
              {/* Left Column connections starting from Center Core (300, 165) */}
              <path d="M 300,165 Q 180,25 155,25" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 160,65 125,65" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 180,105 105,105" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 190,145 92,145" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 180,185 92,185" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 190,225 105,225" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 170,265 125,265" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 180,305 155,305" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />

              {/* Right Column connections starting from Center Core (300, 165) */}
              <path d="M 300,165 Q 420,25 445,25" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 440,65 475,65" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 420,105 495,105" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 410,145 508,145" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 420,185 508,185" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 410,225 495,225" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 430,265 475,265" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
              <path d="M 300,165 Q 420,305 445,305" stroke="url(#clinical-glow-lines)" strokeWidth="1" strokeDasharray="3 3" />
            </svg>

            {/* Left Symmetrical Column of Iraqi Governorates - Arranged as an elegant Left Arc `<` */}
            <div className="absolute left-[2%] lg:left-[5%] top-0 bottom-0 flex flex-col justify-between py-1.5 hidden md:flex w-[160px] text-right z-20 pointer-events-auto">
              
              {/* 1. بغداد (المركز) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-indigo-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-12"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">بغداد (المركز)</div>
                  <div className="text-[7px] font-mono font-bold text-indigo-500 tracking-tight mt-0.5">IQ-BGD • 42ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-500"></span>
                </span>
              </motion.div>

              {/* 2. البصرة (الجنوب) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-emerald-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-8"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">البصرة (الجنوب)</div>
                  <div className="text-[7px] font-mono font-bold text-emerald-600 tracking-tight mt-0.5">IQ-BSR • 68ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
              </motion.div>

              {/* 3. النجف الأشرف */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-rose-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-4"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">النجف الأشرف</div>
                  <div className="text-[7px] font-mono font-bold text-rose-500 tracking-tight mt-0.5">IQ-NJF • 55ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500"></span>
                </span>
              </motion.div>

              {/* 4. كربلاء المقدسة */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-teal-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-1"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">كربلاء المقدسة</div>
                  <div className="text-[7px] font-mono font-bold text-teal-600 tracking-tight mt-0.5">IQ-KBL • 53ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-500"></span>
                </span>
              </motion.div>

              {/* 5. بابل (الحلة) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-indigo-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-0"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">بابل (الحلة)</div>
                  <div className="text-[7px] font-mono font-bold text-indigo-600 tracking-tight mt-0.5">IQ-BBL • 51ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-500"></span>
                </span>
              </motion.div>

              {/* 6. ذي قار (الناصرية) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-rose-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-1"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">ذي قار (الناصرية)</div>
                  <div className="text-[7px] font-mono font-bold text-rose-500 tracking-tight mt-0.5">IQ-DQR • 70ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500"></span>
                </span>
              </motion.div>

              {/* 7. ميسان (العمارة) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-amber-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-4"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">ميسان (العمارة)</div>
                  <div className="text-[7px] font-mono font-bold text-amber-500 tracking-tight mt-0.5">IQ-MSN • 65ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
                </span>
              </motion.div>

              {/* 8. المثنى (السماوة) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-emerald-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-8"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">المثنى (السماوة)</div>
                  <div className="text-[7px] font-mono font-semibold text-emerald-600 tracking-tight mt-0.5">IQ-MTH • 72ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
              </motion.div>

              {/* 9. القادسية (الديوانية) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-purple-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-end translate-x-12"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: 2 }}
              >
                <div className="text-right">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">القادسية (الديوانية)</div>
                  <div className="text-[7px] font-mono font-bold text-purple-600 tracking-tight mt-0.5">IQ-QAS • 58ms</div>
                </div>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-500"></span>
                </span>
              </motion.div>

            </div>

            {/* Central Diagnostic Active Core Logo Wrapper */}
            <div className="relative z-30 flex flex-col items-center justify-center gap-2.5">
              <div className="relative flex items-center justify-center">
                
                {/* Outer active pulse sonar ripple - highly visible on mobile */}
                <motion.div
                  className="absolute -inset-5 rounded-[32px] border border-cyan-400/40 pointer-events-none"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.4, 0.85, 0.4],
                  }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                
                {/* Secondary active pulse sonar ripple (staggered) - highly visible on mobile */}
                <motion.div
                  className="absolute -inset-8 rounded-[40px] border border-indigo-400/25 pointer-events-none"
                  animate={{
                    scale: [1, 1.22, 1],
                    opacity: [0.2, 0.7, 0.2],
                  }}
                  transition={{
                    duration: 3.5,
                    delay: 1.75,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />

                {/* Vibrant ambient neon diagnostics background glow - adjusted for perfect mobile contrast */}
                <motion.div
                  className="absolute -inset-4 rounded-[28px] bg-gradient-to-tr from-indigo-500/35 via-cyan-400/30 to-purple-500/35 opacity-90 blur-xl"
                  animate={{
                    scale: [1, 1.15, 1],
                    rotate: [0, 180, 360],
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                
                <motion.div 
                  className="w-28 h-28 bg-white rounded-3xl p-1.5 shadow-md border border-slate-100 flex items-center justify-center relative select-none"
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <img 
                    src="/logo.png" 
                    alt="الدكتور واثق الجلبي" 
                    className="w-full h-full object-cover rounded-[18px]" 
                    referrerPolicy="no-referrer"
                  />
                </motion.div>
              </div>


            </div>

            {/* Right Symmetrical Column of Iraqi Governorates - Arranged as an elegant Right Arc `>` */}
            <div className="absolute right-[2%] lg:right-[5%] top-0 bottom-0 flex flex-col justify-between py-1.5 hidden md:flex w-[160px] text-left z-20 pointer-events-auto">
              
              {/* 10. أربيل (الشمال) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-cyan-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-12"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">أربيل (الشمال)</div>
                  <div className="text-[7px] font-mono font-bold text-cyan-600 tracking-tight mt-0.5">IQ-EBL • 58ms</div>
                </div>
              </motion.div>

              {/* 11. نينوى (الموصل) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-amber-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-8"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">نينوى (الموصل)</div>
                  <div className="text-[7px] font-mono font-bold text-amber-600 tracking-tight mt-0.5">IQ-MSL • 61ms</div>
                </div>
              </motion.div>

              {/* 12. السليمانية */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-purple-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-4"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">السليمانية</div>
                  <div className="text-[7px] font-mono font-bold text-purple-500 tracking-tight mt-0.5">IQ-SLI • 66ms</div>
                </div>
              </motion.div>

              {/* 13. كركوك والوسط */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-orange-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start r-translate-x-1 -translate-x-1"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">كركوك والوسط</div>
                  <div className="text-[7px] font-mono font-bold text-orange-600 tracking-tight mt-0.5">IQ-KRK • 60ms</div>
                </div>
              </motion.div>

              {/* 14. دهوك */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-teal-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start translate-x-0"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">دهوك</div>
                  <div className="text-[7px] font-mono font-bold text-teal-600 tracking-tight mt-0.5">IQ-DHK • 62ms</div>
                </div>
              </motion.div>

              {/* 15. الأنبار */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-indigo-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-1"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">الأنبار</div>
                  <div className="text-[7px] font-mono font-bold text-indigo-600 tracking-tight mt-0.5">IQ-ANB • 59ms</div>
                </div>
              </motion.div>

              {/* 16. ديالى */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-pink-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-4"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pink-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">ديالى</div>
                  <div className="text-[7px] font-mono font-bold text-pink-600 tracking-tight mt-0.5">IQ-DYL • 49ms</div>
                </div>
              </motion.div>

              {/* 17. صلاح الدين */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-rose-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-8"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">صلاح الدين</div>
                  <div className="text-[7px] font-mono font-bold text-rose-600 tracking-tight mt-0.5">IQ-SAD • 54ms</div>
                </div>
              </motion.div>

              {/* 18. واسط (الكوت) */}
              <motion.div 
                className="bg-white/85 backdrop-blur-md border border-emerald-100/70 rounded-lg px-2 py-1 shadow-xs flex items-center gap-1.5 self-start -translate-x-12"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                whileHover={{ scale: 1.05, x: -2 }}
              >
                <span className="flex h-1.5 w-1.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                <div className="text-left">
                  <div className="text-[9.5px] font-black text-slate-800 leading-none">واسط (الكوت)</div>
                  <div className="text-[7px] font-mono font-bold text-emerald-600 tracking-tight mt-0.5">IQ-WST • 47ms</div>
                </div>
              </motion.div>

            </div>

          </div>

          <motion.div 
            className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-150 px-3.5 py-1.5 rounded-full text-indigo-700 text-[10.5px] font-extrabold mb-5"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
          >
            <HeartPulse className="w-3.5 h-3.5 text-red-500 animate-pulse" />
            منظومة الرعاية الصحية الذكية
          </motion.div>
          
          <motion.h2 
            className="text-3xl md:text-4.5xl font-black text-slate-900 mb-4 leading-tight tracking-tight"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
          >
            مرحباً بكم في موقع <span className="text-indigo-600">الدكتور واثق الجلبي</span>
          </motion.h2>
        </div>

        {/* Major Action Cards Grid (SPOTLIGHT COMPONENT with staggered grid entrance) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl mb-10">
          
          {/* Card 1: Free AI Consultation */}
          <motion.div 
            className="bg-white border border-slate-200 hover:border-indigo-300 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative overflow-hidden ring-offset-2 hover:ring-2 hover:ring-indigo-100"
            initial={{ opacity: 0, x: 25 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            onMouseEnter={playAiHoverSound}
          >
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-indigo-50 rounded-full opacity-60 group-hover:scale-130 transition-transform duration-500" />
            {/* Tech scanning scanner line */}
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-400 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            
            <div>
              <img src="/alaa_avatar.png?v=4" alt="آلاء" className="w-12 h-12 rounded-full object-cover mb-4 border-2 border-indigo-100 shadow-sm drop-shadow-[0_2px_4px_rgba(99,102,241,0.3)]" />
              <h3 className="font-extrabold text-[#0f172a] text-sm md:text-base mb-2">مساعدة الاستعلامات (آلاء) - Reception</h3>
              <p className="text-slate-500 text-[11px] leading-relaxed mb-3 font-semibold">
                اطرح استفساراتك وسأقوم بمساعدتك فوراً في: حجز المواعيد، أخذ تاريخك المرضي الشامل، ورفع كافة ملفاتك الطبية من صور وتقارير لعرضها على الطبيب.
              </p>
            </div>
            <button 
              onClick={handleOpenChat}
              className="w-full bg-[#0F172A] hover:bg-slate-800 text-white font-extrabold py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-2 transition active:scale-95 cursor-pointer shadow-sm mt-2 relative z-10"
            >
              <Sparkles className="w-4 h-4 text-indigo-300" />
              <span>تحدث مع آلاء الآن</span>
            </button>
          </motion.div>

          {/* Card 2: Live Video Consultation */}
          <motion.div 
            className="bg-white border border-slate-200 hover:border-rose-300 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative overflow-hidden ring-offset-2 hover:ring-2 hover:ring-rose-100"
            initial={{ opacity: 0, x: -25 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            onMouseEnter={playAiHoverSound}
          >
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-rose-50 rounded-full opacity-60 group-hover:scale-130 transition-transform duration-500" />
            {/* Tech scanning scanner line */}
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-rose-400 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            
            <div>
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mb-4 border border-rose-100">
                <Video className="w-5.5 h-5.5" />
              </div>
              <h3 className="font-extrabold text-[#0f172a] text-sm md:text-base mb-2">استشارة فيديو مباشرة</h3>
              <p className="text-slate-500 text-[11px] leading-relaxed mb-3 font-semibold">
                تحدث صوتاً وصورة مباشرة وبشكل آمن مع طبيبنا الممارس لمناقشة تقاريرك، ومراجعة نتائج الفحوصات الطبية. غير مخصصة للحالات المرضية الحادة حيث تحتاج معاينة مباشرة من قبل الطبيب.
              </p>
            </div>
            <button 
              onClick={handleOpenVideo}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-extrabold py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-1.5 transition active:scale-95 cursor-pointer shadow-sm mt-2 relative z-10"
            >
              <Video className="w-4 h-4 text-rose-200" />
              <span>احجز مكالمة فيديو مباشرة</span>
            </button>
          </motion.div>

        </div>

        {/* Patient Dashboard Access & Configuration Gateway - ADDITIVE PORTAL */}
        <motion.div 
          className="w-full max-w-3xl bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mb-10 text-right font-sans"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.6, ease: "easeOut" }}
          id="patient-dashboard-gateway"
        >
          <div className="border-b border-slate-100 pb-3 mb-5 flex justify-between items-start">
            <div>
              <span className="text-[10px] text-teal-600 font-extrabold tracking-wider uppercase block">بوابة المريض الرقمية (EHR Dashboard)</span>
              <h3 className="text-base font-black text-slate-900 flex items-center gap-1.5 justify-start">
                🪪 لوحة المريض الإلكترونية والاستعلام السريري
              </h3>
            </div>
            
            {currentUser ? (
              <div className="flex flex-col items-end gap-1">
                <span className="text-[10px] bg-teal-50 text-teal-700 font-bold px-2 py-1 rounded-md border border-teal-100 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  حساب موثق: {currentUser.email}
                </span>
                <button
                  onClick={() => signOut(auth)}
                  className="text-[10px] text-rose-600 hover:text-rose-800 font-extrabold flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <LogOut className="w-3 h-3" /> خروج من الحساب
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-lg text-xs font-black transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <LogIn className="w-3.5 h-3.5" />
                الدخول بالحساب (جديد)
              </button>
            )}
          </div>

          {!patientData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              {/* البطاقة الأولى (يمين): الاستعلام السريع برقم الهاتف/التليجرام */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm relative overflow-hidden h-full flex flex-col">
                <div className="absolute top-0 right-0 w-1.5 h-full bg-teal-500"></div>
                <h4 className="text-sm font-black text-slate-800 mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-teal-600" />
                  الاستعلام السريع للزوار
                </h4>

                {requireTelegramLink ? (
                  <div className="space-y-4">
                <div className="bg-rose-50 border border-rose-200 text-rose-900 p-4 rounded-xl text-center space-y-3">
                  <div className="text-xl">⚠️</div>
                  <p className="text-xs font-bold leading-relaxed">
                    عذراً {tempPatientName || "عزيزنا المراجع"}، لم نجد ربطاً نشطاً لحسابك بالتليجرام في السجلات الطبية للعيادة.
                    للحفاظ على خصوصية وسرية بياناتك الطبية، يسرنا جداً تفعيل الخدمة مجاناً بخطوة واحدة بسيطة بالضغط على الزر أدناه والبدء في محادثة البوت.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                    <a
                      href={`https://t.me/IQDoctorClinic_bot?start=${tempPatientId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs px-5 py-3 rounded-xl transition inline-flex items-center gap-2 justify-center"
                    >
                      <span>تفعيل التنبيهات الطبية عبر تليجرام 📱</span>
                    </a>
                    <button
                      type="button"
                      onClick={async () => {
                        setLookupLoading(true);
                        setLookupError(null);
                        try {
                          const res = await fetch("/api/patient/check-telegram-status", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ patientId: tempPatientId })
                          });
                          const statusData = await res.json();
                          if (res.ok && statusData.success && statusData.telegramChatId) {
                            setRequireTelegramLink(false);
                            setNeedOtp(true);
                            // Retrigger OTP code dispatch securely 
                            await handleLookupPatient(null as any, false);
                          } else {
                            setLookupError("البوت لم يتلقى أمر التفعيل منك بعد. يرجى الضغط على الزر في الأعلى ثم إرسال /start في البوت أولاً.");
                          }
                        } catch (e) {
                          console.error(e);
                          setLookupError("فشل فحص الحالة، يرجى تكرار المحاولة لاحقاً.");
                        } finally {
                          setLookupLoading(false);
                        }
                      }}
                      disabled={lookupLoading}
                      className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-extrabold text-xs px-5 py-3 rounded-xl transition inline-flex items-center gap-1.5 justify-center cursor-pointer"
                    >
                      {lookupLoading ? "جاري تتبع التنشيط..." : "تأكيد التفعيل وإعادة المحاولة 🔄"}
                    </button>
                  </div>
                  {lookupError && (
                    <p className="text-rose-600 text-[11px] font-bold mt-2 bg-white px-2.5 py-1.5 rounded-lg border border-rose-100">{lookupError}</p>
                  )}
                </div>
                <div className="flex justify-end pt-2 border-t border-slate-100 items-center">
                  <div className="flex-1 text-right">
                    <button
                      type="button"
                      onClick={handleRequestSms}
                      disabled={lookupLoading}
                      className="text-xs font-bold text-amber-600 hover:text-amber-800 transition cursor-pointer flex items-center gap-1"
                    >
                      📩 أو اضغط هنا لطلب الرمز عبر رسالة نصية (SMS)
                    </button>
                    <div id="recaptcha-container"></div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setRequireTelegramLink(false);
                      setNeedOtp(false);
                      setLookupError(null);
                    }}
                    className="text-xs font-bold text-slate-500 hover:text-slate-800 transition cursor-pointer"
                  >
                    ← تراجع والرجوع للبحث
                  </button>
                </div>
              </div>
            ) : needOtp ? (
              <form onSubmit={(e) => handleLookupPatient(e, true)} className="flex-1 flex flex-col space-y-4">
                <div className="bg-indigo-50 border border-indigo-150 p-4 rounded-xl space-y-2">
                  <span className="text-indigo-950 font-black text-xs block">🔐 التحقق الأمني الثنائي:</span>
                  <p className="text-slate-600 text-[11px] leading-relaxed font-semibold">
                    لقد قمنا بإرسال رمز دخول سري مؤقت (OTP) إلى حساب تليجرام الموثق والخاص بملفك الطبي. صالح لمدة 10 دقائق فقط.
                  </p>
                </div>
                
                <div className="mt-auto">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <input 
                      type="text"
                      maxLength={6}
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value)}
                      placeholder="أدخل رمز التحقق المكون من 6 أرقام..."
                      className="flex-1 w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-xs outline-none focus:border-teal-500 focus:bg-white text-center font-mono font-black tracking-widest"
                    />
                    <button
                      type="submit"
                      disabled={lookupLoading || otpValue.trim().length !== 6}
                      className="w-full sm:w-auto flex items-center justify-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-extrabold text-xs px-6 py-3 rounded-xl transition cursor-pointer select-none shrink-0"
                    >
                      <span>{lookupLoading ? "جاري التحقق..." : "تأكيد الرمز والدخول"}</span>
                      <span>🔑</span>
                    </button>
                  </div>

                  {lookupError && (
                    <p className="text-rose-600 text-[11px] font-bold mt-1 bg-rose-50 p-2.5 rounded-lg border border-rose-100">{lookupError}</p>
                  )}

                  <div className="flex justify-between items-center pt-2 border-t border-slate-100 text-[11px] mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleLookupPatient(null as any, false);
                      }}
                      className="text-teal-600 font-extrabold hover:underline cursor-pointer"
                    >
                      لم تستلم الرمز؟ أعِد الإرسال 🔄
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNeedOtp(false);
                        setOtpValue("");
                        setLookupError(null);
                      }}
                      className="text-slate-500 font-extrabold hover:underline cursor-pointer"
                    >
                      تغيير رقم الاستعلام ←
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={(e) => handleLookupPatient(e, false)} className="flex-1 flex flex-col space-y-4">
                <p className="text-slate-500 text-[11px] leading-relaxed font-semibold">
                  يقدم نظام IQ-Doctor بوابة سريرية خاصة للمراجعين تُمكّنك من الاستعلام عن مواعيدك السابقة والقادمة وتتبع حالاتها، بالإضافة إلى التسجيل في خدمة التنبيهات والرسائل الفورية للتذكير الطبي. وللحفاظ على خصوصية وسرية المعلومات، سوف يتم إرسال رمز تحقق آمن ومؤقت على رقم هاتفك المشترك في تطبيق التليجرام لتأكيد عملية الدخول.
                </p>
                
                <div className="mt-auto space-y-3">
                  <div>
                    <button
                      type="button"
                      onClick={handleRequestSms}
                      disabled={lookupLoading}
                      className="text-[11px] font-bold text-amber-600 hover:text-amber-800 transition cursor-pointer flex items-center gap-1"
                    >
                      📩 أو اضغط هنا لطلب الرمز عبر رسالة نصية (SMS)
                    </button>
                    <div id="recaptcha-container"></div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <input 
                      type="text"
                      value={lookupId}
                      onChange={(e) => setLookupId(e.target.value)}
                      placeholder="أدخل رقم الهاتف أو الرقم الوطني..."
                      className="flex-1 w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3.5 text-xs outline-none focus:border-teal-500 focus:bg-white text-right font-sans font-bold"
                    />
                    <button
                      type="submit"
                      disabled={lookupLoading || !lookupId.trim()}
                      className="w-full sm:w-auto flex items-center justify-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-extrabold text-xs px-6 py-3.5 rounded-xl transition cursor-pointer select-none shrink-0"
                    >
                      <span>{lookupLoading ? "جاري الاستعلام..." : "استعلام ودخول آمن"}</span>
                      <span>🔎</span>
                    </button>
                  </div>

                  {lookupError && (
                    <p className="text-rose-600 text-[11px] font-bold mt-1 bg-rose-50 p-2.5 rounded-lg border border-rose-100">{lookupError}</p>
                  )}
                </div>
              </form>
            )}
            </div>

            {/* البطاقة الثانية (يسار): الحساب الشخصي بالبريد الإلكتروني */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-2xl p-5 shadow-sm relative overflow-hidden flex flex-col h-full">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-indigo-500"></div>
              
              <h4 className="text-sm font-black text-indigo-900 mb-4 flex items-center gap-2">
                <UserCircle className="w-4 h-4 text-indigo-600" />
                الحساب الشخصي (موصى به ⭐)
              </h4>
              
              {currentUser ? (
                <div className="flex-1 flex flex-col justify-center items-center text-center space-y-3 py-4">
                  <ShieldCheck className="w-10 h-10 text-teal-500 mb-1" />
                  <p className="text-xs font-bold text-slate-700">أهلاً بك، تم توثيق حسابك بنجاح!</p>
                  <span className="text-[11px] bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 w-full truncate" dir="ltr">{currentUser.email}</span>
                  <p className="text-[10px] text-slate-500 mt-2">يرجى إدخال رقم هاتفك أو الرقم الوطني في بطاقة الاستعلام الجانبية لربط وعرض ملفك الطبي.</p>
                </div>
              ) : (
                <>
                  <ul className="text-[11px] text-slate-700 space-y-3 mb-6 flex-1 mt-2">
                    <li className="flex items-start gap-2">
                      <div className="mt-0.5 min-w-[14px]"><Sparkles className="w-3.5 h-3.5 text-indigo-600" /></div>
                      <span><strong className="text-indigo-900 block mb-0.5">سجلك الطبي الشامل:</strong>احتفظ بجميع الوصفات الطبية والتقارير في مكان واحد آمن.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-0.5 min-w-[14px]"><Calendar className="w-3.5 h-3.5 text-indigo-600" /></div>
                      <span><strong className="text-indigo-900 block mb-0.5">إدارة المواعيد:</strong>تتبع أو تعديل أو إلغاء حجوزاتك القادمة والسابقة بسهولة.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-0.5 min-w-[14px]"><HeartPulse className="w-3.5 h-3.5 text-indigo-600" /></div>
                      <span><strong className="text-indigo-900 block mb-0.5">أولوية التواصل:</strong>توثيق لحالتك لضمان متابعة دقيقة من قبل الدكتور.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-0.5 min-w-[14px]"><ShieldCheck className="w-3.5 h-3.5 text-indigo-600" /></div>
                      <span><strong className="text-indigo-900 block mb-0.5">خصوصية تامة:</strong>ملف رقمي محمي بالكامل ومربوط ببريدك الإلكتروني.</span>
                    </li>
                  </ul>
                  
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs py-3.5 rounded-xl transition-colors shadow-md flex justify-center items-center gap-2 mt-auto"
                  >
                    <LogIn className="w-4 h-4" />
                    تسجيل الدخول / إنشاء حساب جديد
                  </button>
                </>
              )}
            </div>
          </div>
          ) : (
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Family Switcher */}
              {allFamilyMembers.length > 1 && (
                <div className="bg-white border border-indigo-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-bold text-slate-700">تصفح ملف:</span>
                  </div>
                  <div className="flex gap-2 flex-wrap flex-1 justify-end">
                    {allFamilyMembers.map(member => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => {
                          setPatientData(member);
                          setAppointmentsList(allAppointments.filter((a: any) => a.patientId === member.id));
                          setRecordsList(allRecords.filter((r: any) => r.patientId === member.id));
                          setOrdersList(allOrders.filter((o: any) => o.patientId === member.id));
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer border ${patientData.id === member.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-indigo-50 hover:text-indigo-700'}`}
                      >
                        {member.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {currentUser && (
                <div className="flex justify-end">
                  <button 
                    onClick={() => setIsAddFamilyModalOpen(true)}
                    className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold px-3 py-1.5 rounded-lg border border-indigo-200 flex items-center gap-1.5 transition cursor-pointer shadow-sm"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    إضافة فرد جديد للعائلة
                  </button>
                </div>
              )}

              {/* Patient Banner Metadata Block */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
                
                {currentUser && (
                  <div className="absolute top-4 left-4 z-10 flex flex-col items-end gap-1.5">
                    {patientData.profileImageUrl && (
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-md">
                        <img src={patientData.profileImageUrl} alt="صورة المريض" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <label className="text-[10px] bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-bold px-2 py-1 rounded-md cursor-pointer transition shadow-sm flex items-center gap-1">
                      {isUploadingAvatar ? "جاري الرفع..." : "📸 تغيير الصورة"}
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handleAvatarUpload}
                        disabled={isUploadingAvatar}
                      />
                    </label>
                    {avatarUploadError && <span className="text-[9px] text-rose-600 font-bold mt-1 bg-white px-1 rounded">{avatarUploadError}</span>}
                  </div>
                )}

                <div className="space-y-1 pr-6">
                  <span className="text-[10px] text-slate-400 font-bold block">الاسم الكامل للمراجع:</span>
                  <span className="text-xs font-black text-slate-800 block">{patientData.name}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold block">رقم الملف الموحد:</span>
                  <span className="text-xs font-mono font-bold text-teal-600 block">#{patientData.id}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold block">رقم الهاتف المسجل لدينا:</span>
                  {!isEditingPhone ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-700 block">{patientData.phone}</span>
                      {currentUser && (
                        <button 
                          onClick={() => { setNewPhoneValue(patientData.phone); setIsEditingPhone(true); setUpdatePhoneError(""); }}
                          className="text-[10px] text-indigo-600 hover:text-indigo-800 underline font-bold"
                        >
                          تعديل
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5 mt-1">
                      <input 
                        type="text" 
                        value={newPhoneValue} 
                        onChange={(e) => setNewPhoneValue(e.target.value)} 
                        className="w-full text-xs font-mono px-2 py-1.5 border border-slate-300 rounded focus:border-indigo-500 outline-none"
                        placeholder="07..."
                      />
                      {updatePhoneError && <span className="text-[10px] text-red-500 font-bold">{updatePhoneError}</span>}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={async () => {
                            setUpdatePhoneError("");
                            if (!/^07[0-9]{9}$/.test(newPhoneValue)) {
                              setUpdatePhoneError("رقم غير صحيح");
                              return;
                            }
                            setIsUpdatingPhone(true);
                            try {
                              const res = await fetch("/api/patient/update-phone", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ patientId: patientData.id, newPhone: newPhoneValue, email: currentUser.email, firebaseVerified: true })
                              });
                              const data = await res.json();
                              if (data.success) {
                                setPatientData((prev: any) => ({ ...prev, phone: newPhoneValue }));
                                setAllFamilyMembers((prev: any[]) => prev.map(p => p.id === patientData.id ? { ...p, phone: newPhoneValue } : p));
                                setIsEditingPhone(false);
                              } else {
                                setUpdatePhoneError(data.error || "فشل");
                              }
                            } catch (e) {
                              setUpdatePhoneError("خطأ");
                            } finally {
                              setIsUpdatingPhone(false);
                            }
                          }}
                          disabled={isUpdatingPhone}
                          className="text-[10px] bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 font-bold disabled:opacity-50"
                        >
                          {isUpdatingPhone ? "..." : "حفظ"}
                        </button>
                        <button 
                          onClick={() => setIsEditingPhone(false)}
                          className="text-[10px] bg-slate-200 text-slate-700 px-2 py-1 rounded hover:bg-slate-300 font-bold"
                        >
                          إلغاء
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold block">الرقم الوطني الموحد:</span>
                  <span className="text-xs font-mono text-slate-700 block text-right">
                    {patientData.nationalId || "غير متوفر (يرجى مراجعة موظف الاستعلام لتسجيله)"}
                  </span>
                </div>
              </div>

              {/* Telegram Notifications Integration Control Card */}
              <div className="border border-indigo-150 bg-indigo-50/40 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <h4 className="font-bold text-xs text-indigo-950 flex items-center gap-1.5 leading-normal">
                    <span>🔔 تفعيل التنبيهات الطبية عبر تليجرام</span>
                  </h4>
                  <span className="text-[10px] bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-md">مجاني وآمن 🛡️</span>
                </div>
                
                <p className="text-[11px] text-slate-600 leading-relaxed font-semibold">
                  قم بربط حسابك ببوت العيادة على تليجرام لتتلقى إشعارات وتذكيرات فورية ومباشرة على موبايلك في حال تم تأكيد، تعديل، أو مراجعة أي موعد أو استشارة طبية خاصة بك وسجل مراجعاتك.
                </p>

                {patientData.telegramChatId ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-lg flex items-center justify-between flex-wrap gap-2 text-xs font-bold text-right w-full">
                    <span>تم تفعيل خدمة التنبيهات الطبية وتليجرام بنشاط وجاهزية تامة 🟢</span>
                    <button 
                      type="button" 
                      onClick={() => handleRefreshActivation(patientData.id)}
                      className="text-[10px] text-indigo-700 underline hover:text-indigo-900 font-extrabold cursor-pointer"
                    >
                      تحديث الحالة 🔄
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <a
                      href={`https://t.me/IQDoctorClinic_bot?start=${patientData.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto inline-flex bg-indigo-600 hover:bg-indigo-700 text-white font-black py-3 px-6 rounded-xl text-xs items-center justify-center gap-2 hover:shadow-indigo-500/20 transition cursor-pointer select-none"
                    >
                      <span>تفعيل التنبيهات الطبية عبر تليجرام 🔔</span>
                    </a>
                    
                    <div className="bg-indigo-100/30 p-2.5 rounded-lg border border-indigo-150 text-[10.5px] text-indigo-900 leading-relaxed font-medium">
                      📱 <strong>طريقة التفعيل بسيطة:</strong> اضغط على الزر في الأعلى للذهاب للبوت، ثم اضغط على زر <strong>"Start / البدء"</strong> في تطبيق التليجرام. بعد ذلك، يمكنك الرجوع هنا والضغط على زر <span className="underline cursor-pointer font-bold" onClick={() => handleRefreshActivation(patientData.id)}>تحديث الحالة 🔄</span> لتأكيد نجاح التنشيط الفوري!
                    </div>
                  </div>
                )}
              </div>

              {/* Appointments List Render */}
              <div className="space-y-3">
                <h4 className="font-bold text-xs text-slate-800">🗓️ مواعيدي وفحوصاتي الطبية الحالية والسابقة:</h4>
                {appointmentsList.length === 0 ? (
                  <div className="text-center text-slate-400 text-xs italic bg-slate-50 py-6 rounded-xl border border-slate-100">
                    لا توجد مواعيد أو استشارات مسجلة على ملفك الطبي حالياً. يمكن حجز استشارة بالضغط على الأزرار في الأعلى.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {appointmentsList.map((appt: any) => {
                      const apptDateStr = new Date(appt.dateTime).toLocaleString("ar-IQ", {
                        timeZone: "Asia/Baghdad",
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit"
                      });

                      const statusColor = appt.status === "COMPLETED" 
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : appt.status === "MISSED"
                        ? "bg-rose-50 text-rose-700 border-rose-250"
                        : "bg-amber-50 text-amber-700 border-amber-250";

                      const statusNameAr = appt.status === "COMPLETED" 
                        ? "مكتمل / معاين"
                        : appt.status === "MISSED"
                        ? "فائت / غائب"
                        : "انتظار / مؤكد";

                      return (
                        <div key={appt.id} className="flex justify-between items-center border border-slate-200 p-3 bg-white rounded-xl hover:shadow-xs transition text-xs flex-wrap gap-2 text-right">
                          <div className="text-right">
                            <span className="font-black text-slate-800 block">{apptDateStr}</span>
                            <span className="text-[10px] text-slate-400 block mt-0.5">قسم المعاينة: {appt.department || "الطب العام (GP)"}</span>
                          </div>
                          
                          <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold border ${statusColor} shrink-0`}>
                            {statusNameAr}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Prescriptions List Render */}
              <div className="space-y-3 mt-6 pt-4 border-t border-slate-100">
                <h4 className="font-bold text-xs text-slate-800">الوصفات الطبية السابقة:</h4>
                {recordsList.filter((r: any) => {
                  try {
                    const parsed = typeof r.prescriptions === "string" ? JSON.parse(r.prescriptions) : r.prescriptions;
                    return Array.isArray(parsed) && parsed.length > 0;
                  } catch {
                    return false;
                  }
                }).length === 0 && ordersList.filter((o: any) => o.department === "PHARMACY").length === 0 ? (
                  <div className="text-center text-slate-400 text-xs italic bg-slate-50 py-4 rounded-xl border border-slate-100">
                    لا توجد وصفات طبية مسجلة مسبقاً في النظام لهذا المراجع.
                  </div>
                ) : (
                  <div className="space-y-4 max-h-56 overflow-y-auto pr-1">
                    {recordsList.map((record: any) => {
                      let parsedMeds: any[] = [];
                      try {
                        parsedMeds = typeof record.prescriptions === "string" ? JSON.parse(record.prescriptions) : record.prescriptions;
                      } catch {
                        return null;
                      }
                      if (!Array.isArray(parsedMeds) || parsedMeds.length === 0) return null;

                      const visitDateStr = new Date(record.createdAt).toLocaleDateString("ar-IQ", {
                        year: "numeric", month: "short", day: "numeric"
                      });

                      return (
                        <div key={record.id} className="border border-indigo-100 bg-indigo-50/30 rounded-xl p-3 text-right">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-[10px] text-indigo-400 font-bold">بواسطة: {record.physicianName || "طبيب العيادة"}</span>
                            <span className="text-[10px] bg-white border border-indigo-100 px-2 py-0.5 rounded text-indigo-700 font-black">{visitDateStr}</span>
                          </div>
                          <div className="space-y-2">
                            {parsedMeds.map((med: any, i: number) => (
                              <div key={i} className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm flex flex-col gap-1">
                                <span className="font-bold text-xs text-slate-800">{med.name || med.drug || "دواء غير محدد"}</span>
                                <div className="flex items-center gap-2 text-[10px] text-slate-500 font-medium">
                                  <span>الجرعة: {med.dose || med.dosage || "حسب الإرشادات"}</span>
                                  <span>•</span>
                                  <span>التكرار: {med.frequency || "حسب الإرشادات"}</span>
                                </div>
                                <span className="text-[10px] text-slate-400">الاستخدام: {med.instructions || med.instruction || "وفق توجيهات الطبيب"} {med.duration ? `(${med.duration})` : ""}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {ordersList.filter((o: any) => o.department === "PHARMACY").map((order: any) => {
                      const visitDateStr = new Date(order.createdAt).toLocaleDateString("ar-IQ", {
                        year: "numeric", month: "short", day: "numeric"
                      });
                      return (
                        <div key={order.id} className="border border-indigo-100 bg-indigo-50/30 rounded-xl p-3 text-right">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-[10px] text-indigo-400 font-bold">وصفة مرسلة إلكترونياً</span>
                            <span className="text-[10px] bg-white border border-indigo-100 px-2 py-0.5 rounded text-indigo-700 font-black">{visitDateStr}</span>
                          </div>
                          <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm text-xs text-slate-800 whitespace-pre-wrap leading-relaxed font-semibold">
                            {order.instruction}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Orders List Render */}
              <div className="space-y-3 mt-6 pt-4 border-t border-slate-100">
                <h4 className="font-bold text-xs text-slate-800">الفحوصات المطلوبة والنتائج:</h4>
                {ordersList.filter((o: any) => o.department !== "PHARMACY").length === 0 ? (
                  <div className="text-center text-slate-400 text-xs italic bg-slate-50 py-4 rounded-xl border border-slate-100">
                    لا توجد فحوصات أو تحاليل مطلوبة مسبقاً.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {ordersList.filter((o: any) => o.department !== "PHARMACY").map((order: any) => {
                      const orderDateStr = new Date(order.createdAt).toLocaleDateString("ar-IQ", {
                        year: "numeric", month: "short", day: "numeric"
                      });
                      const isCompleted = order.status === "COMPLETED";

                      return (
                        <div key={order.id} className="border border-slate-200 p-3 bg-white rounded-xl text-right">
                          <div className="flex justify-between items-start mb-2 gap-2">
                            <div>
                              <span className="font-black text-xs text-slate-800 block mb-0.5">{order.department === "LAB" ? "🔬 فحص مختبري" : order.department === "NURSING" ? "💉 إجراء تمريضي" : "☢️ فحص إشعاعي"}</span>
                              <span className="text-[10px] text-slate-500 block">التاريخ: {orderDateStr}</span>
                            </div>
                            <span className={`px-2 py-1 text-[9px] font-bold rounded border ${isCompleted ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                              {isCompleted ? "✅ مكتمل" : "⏳ قيد الانتظار"}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 mb-2">
                            <span className="font-bold">المطلوب:</span> {order.instruction}
                          </div>
                          {isCompleted && order.results && (
                            <div className="text-[10px] text-slate-800 bg-emerald-50/50 p-2 rounded border border-emerald-100">
                              <span className="font-bold text-emerald-800 block mb-0.5">النتيجة:</span> 
                              {order.results}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Close Gateways */}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setPatientData(null);
                    setAppointmentsList([]);
                    setRecordsList([]);
                    setOrdersList([]);
                    setLookupError(null);
                  }}
                  className="text-xs font-bold text-slate-500 hover:text-slate-800 transition flex items-center gap-1 cursor-pointer"
                >
                  🚪 الخروج وإغلاق بوابة الاستعلام الآمنة
                </button>
              </div>

            </motion.div>
          )}
        </motion.div>

        {/* Doctor Demo Gateway */}
        <motion.div
           className="w-full max-w-3xl bg-blue-50/50 border border-blue-200/60 rounded-2xl p-5 shadow-xs mb-8 text-right font-sans overflow-hidden transition-all duration-300"
           initial={{ opacity: 0, y: 15 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.68, duration: 0.6, ease: "easeOut" }}
        >
           <button 
             type="button" 
             onClick={() => setShowDoctorDemo(!showDoctorDemo)}
             className="w-full text-right flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer group"
           >
             <div>
               <h4 className="font-black text-slate-900 text-sm flex items-center gap-2 group-hover:text-blue-700 transition-colors">
                 👨‍⚕️ منصة الأطباء | اكتشف مستقبل إدارة العيادات الذكية
               </h4>
               <p className="text-[11px] text-slate-500 font-semibold mt-1 max-w-xl leading-relaxed">
                 انضم إلى بيئة تجريبية حية واختبر كفاءة منظومة IQ-Doctor في إدارة ملفات المرضى والحجوزات والاستشارات عن بُعد.
               </p>
             </div>
             <div className="bg-white border border-blue-200 text-blue-700 text-[10px] font-bold px-3 py-1.5 rounded-lg flex-shrink-0 text-center shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">
               {showDoctorDemo ? "إغلاق البيئة التجريبية" : "الدخول واختبار المنظومة"}
             </div>
           </button>

           {showDoctorDemo && (
             <div className="mt-5 pt-5 border-t border-blue-200/50 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
               <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm space-y-2">
                 <h5 className="font-extrabold text-blue-800 text-xs">مرحباً بك يا دكتور في البيئة التجريبية المتقدمة لمنصة IQ-Doctor</h5>
                 <p className="text-[11px] text-slate-600 leading-relaxed font-semibold">
                   لقد تم تصميم هذه المنصة لتمنحك تجربة واقعية ومباشرة للجيل القادم من النظم الطبية السحابية. يمكنك الآن تقمص دور مدير العيادة واختبار كافة الميزات الحيوية التي تسهل عملك الطبي والإداري.
                 </p>
               </div>

               <div className="space-y-3 px-1">
                 <h6 className="font-bold text-slate-800 text-[11px] flex items-center gap-1.5 border-b border-blue-100 pb-2">
                   🛠️ دليل التجربة السريعة للأطباء:
                 </h6>
                 
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                   <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm leading-relaxed space-y-1">
                     <span className="font-bold text-indigo-700 text-[10px] block">إدارة ملفات المرضى (EHR):</span>
                     <span className="text-[10px] text-slate-500 font-medium block">
                       توجه إلى لوحة التحكم، وقم بإنشاء "ملف مريض افتراضي". جرب تسجيل التاريخ المرضي، وكتابة الوصفات الطبية، وملاحظة كيف يتم حفظ وأرشفة البيانات بسرعة وسلاسة تامة.
                     </span>
                   </div>
                   
                   <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm leading-relaxed space-y-1">
                     <span className="font-bold text-indigo-700 text-[10px] block">الرعاية الصحية عن بُعد (Telehealth):</span>
                     <span className="text-[10px] text-slate-500 font-medium block">
                       اطلع على واجهة استشارات الفيديو المباشرة، وتعرف على مدى سهولة تواصل المريض معك وجهاً لوجه من خلال المنصة وبأعلى معايير الخصوصية والسرية الطبية.
                     </span>
                   </div>

                   <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm leading-relaxed space-y-1">
                     <span className="font-bold text-indigo-700 text-[10px] block">الاستقبال والحجز الذكي:</span>
                     <span className="text-[10px] text-slate-500 font-medium block">
                       اختبر نظام الجدولة الآلي؛ كيف يقوم المساعد الرقمي بتنظيم مواعيد المراجعين وتوزيعها على الكادر الطبي لمنع التكدس وتوفير الوقت.
                     </span>
                   </div>

                   <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm leading-relaxed space-y-1">
                     <span className="font-bold text-indigo-700 text-[10px] block">التقارير والإحصائيات:</span>
                     <span className="text-[10px] text-slate-500 font-medium block">
                       تفقد لوحة البيانات التحليلية التي تمنحك نظرة شاملة على أداء العيادة، أعداد المراجعين اليومية، والتدفقات الإدارية بدقة متناهية.
                     </span>
                   </div>
                 </div>
               </div>

               <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[10px] text-amber-800 font-semibold flex items-start gap-2">
                 <span className="text-amber-500 mt-0.5">🔒</span>
                 <p className="leading-relaxed">
                   <strong className="text-amber-900 font-bold block mb-0.5">تنبيه أمني:</strong>
                   هذه البيئة مخصصة للتجربة والتقييم فقط، يرجى عدم إدخال بيانات حقيقية أو حساسة للمرضى أثناء فترة الاختبار.
                 </p>
               </div>

               <a 
                  href="https://ais-pre-b47bovknoxncst26rcuazw-876590209409.europe-west2.run.app/#login" 
                  target="_blank"
                  className="block w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-center font-bold text-sm py-3 px-4 rounded-xl shadow-md transition-all duration-300 transform hover:-translate-y-0.5 mb-4"
                >
                  🚀 دخول تجريبي للمنصة
                </a>

                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-center space-y-3 shadow-sm">
                 <h6 className="font-black text-indigo-900 text-xs">💼 هل أنت مستعد لامتلاك نسختك الخاصة؟</h6>
                 <p className="text-[10px] text-indigo-700 leading-relaxed font-medium max-w-lg mx-auto">
                   إذا نالت المنظومة رضاك ورغبت في تخصيص نسخة مستقلة بالكامل تحمل اسمك وشعار عيادتك الخاص، أو نسخة بيضاء، يمكنك الضغط على الزر أدناه للتواصل مباشرة مع الإدارة الفنية.
                 </p>
                 <a 
                   href="mailto:iq.doctor.gp@gmail.com?subject=%D8%B7%D9%84%D8%A8%20%D9%86%D8%B3%D8%AE%D8%A9%20%D9%85%D9%86%20%D8%A7%D9%84%D8%A8%D8%B1%D9%86%D8%A7%D9%85%D8%AC%20(%D8%AA%D8%B1%D8%AE%D9%8A%D8%B5%20%D8%B9%D9%8A%D8%A7%D8%AF%D8%A9%20IQ-Doctor)"
                   className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold px-5 py-2.5 rounded-lg shadow-sm transition-colors mt-1"
                 >
                   طلب ترخيص العيادة ✨
                 </a>
                 <p className="text-[10.5px] text-indigo-600 mt-2 font-medium">
                   للاستفسار والطلب راسل الايميل <a href="mailto:iq.doctor.gp@gmail.com" className="font-bold hover:underline">iq.doctor.gp@gmail.com</a>
                 </p>
                 <div className="mt-3 pt-3 border-t border-indigo-100/60">
                   <p className="text-[9px] text-indigo-400 font-medium leading-relaxed max-w-xl mx-auto">
                     جميع حقوق الملكية الفكرية والبرمجية لمنصة IQ-Doctor محفوظة. لا يجوز شرعاً وقانوناً نسخ النظام، أو استنساخ فكرته، أو إعادة بيعه، أو مشاركته مع أي طرف ثالث دون إذن كتابي صريح من المالك. أي تجاوز لذلك يُعد انتهاكاً للأمانة.
                   </p>
                 </div>
               </div>
             </div>
           )}
        </motion.div>



        {/* Operating Hours Banner with subtle fade-in */}
        <motion.div 
          className="w-full max-w-3xl bg-white border border-slate-200 rounded-2xl p-5 shadow-xs mb-8"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6, ease: "easeOut" }}
        >
          <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider border-b border-slate-100 pb-2 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-indigo-500" /> ساعات الدوام
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-600">
            <div className="flex justify-between items-center bg-slate-50 px-3 py-2 rounded font-sans">
              <span className="font-semibold text-slate-500">منظومة الدوام الصباحي:</span>
              <span className="font-extrabold text-slate-800">10:00 ص - 02:00 م</span>
            </div>
            <div className="flex justify-between items-center bg-slate-50 px-3 py-2 rounded font-sans">
              <span className="font-semibold text-slate-500">منظومة الدوام المسائي:</span>
              <span className="font-extrabold text-slate-800">05:00 م - 09:00 م</span>
            </div>
          </div>
          <div className="text-center font-bold text-[10px] bg-red-50 text-red-700 px-3 py-1.5 rounded mt-3">
            عطلة نهاية الأسبوع لجميع كوادر العيادات الخارجية هي يوم الجمعة
          </div>
        </motion.div>

        {/* Contact info Segment with subtle slide in */}
        <motion.div 
          className="bg-slate-900 text-white w-full max-w-3xl rounded-2xl p-6 flex flex-col md:flex-row justify-between items-center gap-6"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex-1 space-y-3">
            <h3 className="text-sm font-black uppercase text-indigo-400 tracking-wider">الموقع</h3>
            <div className="space-y-3 text-slate-300 text-xs font-sans text-right flex flex-col">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span>العراق - بغداد - السيدية</span>
              </div>
              <a href="https://wa.me/9647893804884" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] transition-colors text-white py-2 px-4 rounded-xl w-fit shadow-sm">
                <Phone className="w-4 h-4" />
                <span className="font-bold text-sm tracking-wide">اتصل بنا على الواتساب</span>
              </a>
            </div>
          </div>
          <div className="w-full md:w-auto px-5 py-4 bg-slate-800 rounded-xl overflow-hidden opacity-90 flex items-center justify-center text-center text-slate-400 text-xs border border-slate-700 font-serif font-bold italic">
            IQ-Doctor Secure Clinical Network
          </div>
        </motion.div>

      </main>

      {/* Floating Chat Button for alternative popups with high-tech clinical spring physics */}
      {!isChatOpen && (
        <motion.button 
          onClick={handleOpenChat}
          className="fixed bottom-6 right-6 p-0 rounded-full z-50 flex items-center justify-center cursor-pointer bg-transparent drop-shadow-[0_0_15px_rgba(99,102,241,0.5)] hover:drop-shadow-[0_0_25px_rgba(99,102,241,0.9)] transition-all"
          title="افتح المساعد العائم"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20, delay: 1 }}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/alaa_avatar.png?v=4" alt="Alaa Assistant" className="w-16 h-16 rounded-full object-cover animate-pulse border-2 border-indigo-400/50" style={{ animationDuration: '2.5s' }} />
        </motion.button>
      )}

      {/* Chat Modal Layer */}
      {isChatOpen && (
        <PatientChatbot onClose={() => setIsChatOpen(false)} patientData={patientData} />
      )}

      {/* Online Consultation Modal */}
      {isOnlineVisitOpen && (
        <OnlineConsultation 
          onClose={() => setIsOnlineVisitOpen(false)} 
          onOpenChat={() => {
            setIsOnlineVisitOpen(false);
            setIsChatOpen(true);
          }}
        />
      )}

      {/* Privacy Policy Modal */}
      {isPrivacyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" dir="rtl">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="bg-white border border-slate-150 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-slate-50 border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-indigo-600">
                <ShieldCheck className="w-5 h-5 text-indigo-600 shrink-0" />
                <h3 className="font-black text-[#0f172a] text-sm md:text-base">اتفاقية سياسة الخصوصية وحماية البيانات الطبية (جمهورية العراق)</h3>
              </div>
              <button 
                id="btn-close-privacy"
                onClick={() => {
                  setIsPrivacyOpen(false);
                  setPendingAction(null);
                }}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 p-1.5 rounded-lg transition-colors cursor-pointer shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 text-[#334155] text-xs font-semibold leading-relaxed space-y-4">
              <p className="text-slate-450 text-[10px] bg-slate-100/75 text-slate-600 font-mono inline-block px-2.5 py-1 rounded-full select-none">
                تاريخ آخر تحديث: 26 مايو 2026
              </p>
              
              <p className="border-r-4 border-indigo-500 pr-3.5 text-[#1E293B] font-bold">
                مرحباً بكم في منصتنا للعيادة الافتراضية. نحن نلتزم بأعلى معايير السرية والخصوصية لحماية بياناتكم الشخصية والصحية وفقاً للقوانين العراقية النافذة والسرية المهنية الطبية. توضح هذه الاتفاقية كيفية جمع، استخدام، ومعالجة بياناتكم عند استخدام خدماتنا عبر الذكاء الاصطناعي، محادثات الواتساب (WhatsApp)، استشارات الفيديو، وأنظمة رفع الملفات والتقارير الطبية.
              </p>

              <div className="space-y-3.5 pt-2">
                <div className="bg-indigo-50/40 p-3 rounded-lg border border-indigo-100/50">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-indigo-700">
                    <span className="bg-indigo-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono ring-2 ring-indigo-100">1</span>
                    البيانات التي نقوم بجمعها
                  </h4>
                  <p className="text-slate-500 pr-5 text-[11px] mb-2 font-semibold">لتقديم خدمة طبية افتراضية دقيقة وآمنة، نقوم بجمع واستضافة البيانات التالية:</p>
                  <ul className="list-disc list-inside space-y-1.5 text-slate-600 pr-5 text-[11px] font-semibold">
                    <li><strong className="text-slate-700">البيانات الشخصية الأساسية:</strong> الاسم الكامل (المطابق للهوية الوطنية أو البطاقة الموحدة)، تاريخ الميلاد، الجنس، المحافظة/السكن، ورقم الهاتف المحمول.</li>
                    <li><strong className="text-slate-700">البيانات الصحية والسريرية:</strong> التاريخ الطبي، الأعراض الحالية، الأدوية، الحساسية، والتشخيصات السابقة.</li>
                    <li><strong className="text-slate-700">الملفات المرفوعة:</strong> التقارير الطبية، الروشتات (الوصفات الطبية)، نتائج التحليلات المختبرية، صور الأشعة والسونار، أو صور الآفات الجلدية.</li>
                    <li><strong className="text-slate-700">البيانات الرقمية:</strong> سجلات المحادثات النصية والصوتية عبر الواتساب أو المنصة، ومعرفات الأجهزة المعنية.</li>
                  </ul>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-slate-800">
                    <span className="bg-slate-850 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">2</span>
                    السرية الطبية والامتثال للقانون العراقي
                  </h4>
                  <ul className="list-disc list-inside space-y-1.5 text-slate-600 pr-5 text-[11px] font-semibold">
                    <li><strong className="text-slate-700">السرية المهنية:</strong> نلتزم التزاماً مطلقاً بالسرية الطبية وفقاً لـ <em className="not-italic underline text-indigo-600">قانون مزاولة مهنة الطب العراقي رقم 114 لسنة 1981</em> ونسق السلوك المهني للأطباء في العراق. تُعامل كل معلوماتكم كأسرار طبية يحظر إفشاؤها.</li>
                    <li><strong className="text-slate-700">حجية التعامل الإلكتروني:</strong> يخضع التعامل عبر المنصة لـ <em className="not-italic underline text-indigo-600">قانون التوقيع الإلكتروني والمعاملات الإلكترونية العراقي رقم 78 لسنة 2012</em>، وتُعامل موافقتكم الإلكترونية وبياناتكم المرفوعة بمسؤولية قانونية كاملة.</li>
                  </ul>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-slate-800">
                    <span className="bg-slate-850 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">3</span>
                    معالجة البيانات عبر الذكاء الاصطناعي (AI)
                  </h4>
                  <p className="text-slate-500 pr-5 text-[11px] mb-2 font-semibold">تستخدم المنصة تقنيات الذكاء الاصطناعي كأداة مساعدة ودعم طبي:</p>
                  <ul className="list-disc list-inside space-y-1.5 text-slate-600 pr-5 text-[11px] font-semibold">
                    <li><strong className="text-slate-700">تحليل الأعراض والفرز الطبي:</strong> يقوم الذكاء الاصطناعي بقراءة النصوص والملفات المرفوعة (مثل الصور والتحاليل) لتصنيف الحالة ومساعدة الطبيب العراقي المختص في تسريع التشخيص وتحديد الحالات الطارئة (العلامات الحمراء).</li>
                    <li><strong className="text-slate-700">حظر البيع والتجارة بالبيانات:</strong> يُمنع منعاً باتاً بيع أو مشاركة بياناتكم الطبية. لأغراض تطوير الذكاء الاصطناعي، يتم عزل وحذف الأسماء والأرقام والهويات تماماً (Anonymization) بحيث تصبح البيانات "مجهولة المصدر" ولا يمكن ربطها بكم قانونياً أو شخصياً.</li>
                  </ul>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-slate-800">
                    <span className="bg-slate-850 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">4</span>
                    محادثات الواتساب (WhatsApp) والاستشارات المرئية
                  </h4>
                  <ul className="list-disc list-inside space-y-1.5 text-slate-600 pr-5 text-[11px] font-semibold">
                    <li><strong className="text-slate-700">التواصل عبر الواتساب:</strong> نظراً لشيوع استخدام تطبيق "واتساب" في العراق، فإن المريض يقر بعلمه بأن التطبيق يخضع لشروط شركة (Meta). بمجرد انتقال البيانات من الواتساب إلى خوادم عيادتنا، تدخل فوراً في نظام الحماية الطبي المشفر الخاص بنا.</li>
                    <li><strong className="text-slate-700">استشارات الفيديو المباشرة:</strong> تتم عبر قنوات اتصال مرئية مشفرة بالكامل. لا يتم تسجيل الجلسات المرئية أو الصوتية إلا بطلب طبي صريح لأغراض المتابعة وبعد أخذ موافقة المريض الشفهية أو الخطية الموثقة.</li>
                  </ul>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-slate-800">
                    <span className="bg-slate-850 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">5</span>
                    أمن وحفظ الملفات المرفوعة
                  </h4>
                  <ul className="list-disc list-inside space-y-1.5 text-slate-600 pr-5 text-[11px] font-semibold">
                    <li><strong className="text-slate-700">التخزين الآمن:</strong> يتم حفظ وتخزين التقارير والصور والتحاليل المرفوعة على خوادم سحابية آمنة ومحمية بجدار حماية قوي يمنع الاختراق أو التسريب.</li>
                    <li><strong className="text-slate-700">صلاحيات الوصول:</strong> لا يحق لأي شخص الاطلاع على ملفاتكم الطبية المرفوعة باستثناء الطبيب العراقي المجاز والمسؤول مباشرة عن علاجكم، أو الكادر الطبي المساعد والمصرح له تقنياً.</li>
                  </ul>
                </div>

                <div className="p-3 bg-red-50/60 rounded-lg border border-red-100/80">
                  <h4 className="font-extrabold text-red-900 text-xs mb-1.5 flex items-center gap-1.5">
                    <span className="bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">6</span>
                    إخلاء المسؤولية الطبي (خاص بالعراق)
                  </h4>
                  <ul className="list-disc list-inside space-y-1.5 text-red-800 pr-5 text-[11px] font-semibold">
                    <li><strong className="text-red-950">دور الذكاء الاصطناعي:</strong> التشخيص والوصفة الطبية النهائية يصدران حصراً من طبيب بشري مسجل في نقابة أطباء العراق. الذكاء الاصطناعي لا يغني عن تقييم الطبيب البشري.</li>
                    <li><strong className="text-red-950">الحالات الطارئة:</strong> العيادة الافتراضية ليست مخصصة للطوارئ الطبية الحرجة (مثل النوبات القلبية، الجلطات، الحوادث البليغة). في هذه الحالات، يجب على المريض التوجه فوراً إلى أقرب مستشفى حكومي أو قطاع خاص أو الاتصال بالإسعاف الفوري (122).</li>
                  </ul>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-slate-800">
                    <span className="bg-slate-850 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">7</span>
                    حقوق المريض
                  </h4>
                  <ul className="list-disc list-inside space-y-1.5 text-slate-600 pr-5 text-[11px] font-semibold">
                    <li>الاطلاع على ملفكم الطبي الرقمي والحصول على نسخة من تقاريركم.</li>
                    <li>تصحيح أو تعديل أي بيانات مغلوطة في السجل.</li>
                    <li>طلب إتلاف أو حذف الحساب والبيانات (مع مراعاة التعليمات الوزارية التي تلزم العيادات بحفظ السجلات الطبية لمدد قانونية محددة).</li>
                  </ul>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
                  <h4 className="font-extrabold text-[#0f172a] text-xs mb-1.5 flex items-center gap-1.5 text-slate-800">
                    <span className="bg-slate-855 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">8</span>
                    الإقرار والموافقة
                  </h4>
                  <p className="text-slate-600 pr-5 text-[11px] font-semibold">
                    باستخدامكم للعيادة عبر الواتساب، أو رفع ملفاتكم، أو الدخول في استشارة الفيديو، فإنكم تقرون بالموافقة الكاملة على شروط هذه الاتفاقية وبما يتوافق مع القوانين والأنظمة المعمول بها في جمهورية العراق.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Footer containing OK button */}
            <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex justify-end">
              <button 
                id="btn-confirm-privacy"
                onClick={() => {
                  try {
                    localStorage.setItem("iq_doctor_privacy_accepted", "true");
                  } catch (e) {
                    console.error("Could not save privacy consent", e);
                  }
                  setIsPrivacyOpen(false);
                  if (pendingAction === "chat") {
                    setIsChatOpen(true);
                  } else if (pendingAction === "video") {
                    setIsOnlineVisitOpen(true);
                  }
                  setPendingAction(null);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2.5 px-6 rounded-xl transition-all cursor-pointer active:scale-95 shadow-sm"
              >
                أوافق وأفهم الشروط
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      
      <MedicalNewsBanner whiteLabel={whiteLabel} onOpenDoctorsPortal={() => {
        setIsDoctorsPortalOpen(true);
        window.scrollTo(0, 0);
      }} />

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 text-center mt-auto flex flex-col items-center gap-2">
        <p className="text-slate-500 text-[11px] font-bold" dir="ltr">
          © 2026 All rights reserved IQ-Doctor system.
        </p>
        <p className="text-[11px] flex items-center justify-center">
          <button 
            id="btn-privacy-policy"
            onClick={() => setIsPrivacyOpen(true)} 
            className="text-indigo-600 hover:text-indigo-700 font-extrabold hover:underline transition-colors cursor-pointer"
          >
            اتفاقية سياسة الخصوصية وحماية البيانات الطبية
          </button>
        </p>
        <div className="text-[#64748B] font-medium leading-relaxed text-[11px] flex flex-col items-center gap-1 text-center">
          <p className="text-slate-700 font-bold" dir="rtl">
            الدكتور واثق عبدالخالق هاشم الجلبي رقم التسجيل 17582
          </p>
          <p className="font-sans text-slate-700 font-semibold text-[11.5px]" dir="ltr">
            Dr. Watheq A. Hashim Al-Chalabi Reg. No. 17582 M.B.Ch.B, RCPI, Dip. Dermatology
          </p>
        </div>
      </footer>

      {/* Add Family Member Modal */}
      {isAddFamilyModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50">
              <h2 className="font-black text-slate-800 text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                إضافة فرد للعائلة
              </h2>
              <button onClick={() => setIsAddFamilyModalOpen(false)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddFamilySubmit} className="p-5 space-y-4">
              {addFamilyError && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 p-3 rounded-xl text-xs font-bold text-center">
                  {addFamilyError}
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">صلة القرابة</label>
                <select
                  value={newFamilyMember.relationship}
                  onChange={(e) => setNewFamilyMember({...newFamilyMember, relationship: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white cursor-pointer"
                >
                  <option value="ابن">ابن</option>
                  <option value="بنت">بنت</option>
                  <option value="زوجة">زوجة</option>
                  <option value="غير ذلك">غير ذلك</option>
                </select>
              </div>
              {(newFamilyMember.relationship === 'زوجة' || newFamilyMember.relationship === 'غير ذلك') && (
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600">رقم الهاتف <span className="text-rose-500">*</span></label>
                  <input
                    type="tel"
                    required
                    value={newFamilyMember.phone}
                    onChange={(e) => setNewFamilyMember({...newFamilyMember, phone: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white text-left font-mono"
                    placeholder="07XXXXXXXXX"
                    dir="ltr"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">الاسم الثلاثي للطفل/الفرد</label>
                <input
                  type="text"
                  required
                  value={newFamilyMember.name}
                  onChange={(e) => setNewFamilyMember({...newFamilyMember, name: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white"
                  placeholder="مثال: يوسف أحمد محمد"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">سنة الميلاد</label>
                <input
                  type="number"
                  required
                  min="1900"
                  max={new Date().getFullYear()}
                  value={newFamilyMember.dob}
                  onChange={(e) => setNewFamilyMember({...newFamilyMember, dob: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white text-left"
                  placeholder="2010"
                  dir="ltr"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">
                  الرقم الوطني الموحد
                  {(newFamilyMember.relationship === 'زوجة' || newFamilyMember.relationship === 'غير ذلك') && <span className="text-rose-500"> *</span>}
                </label>
                <input
                  type="text"
                  required={newFamilyMember.relationship === 'زوجة' || newFamilyMember.relationship === 'غير ذلك'}
                  value={newFamilyMember.nationalId}
                  onChange={(e) => setNewFamilyMember({...newFamilyMember, nationalId: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white text-left font-mono"
                  placeholder="اختياري"
                  dir="ltr"
                />
              </div>
              <button
                type="submit"
                disabled={addFamilyLoading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold py-3 rounded-xl text-sm transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
              >
                {addFamilyLoading ? 'جاري الإضافة...' : 'حفظ الملف وإضافته'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
